import { cloneDeep } from 'lodash';
import { useState, useEffect } from 'react';
import { useAppSelector } from '../../common/hooks';
import {
    GoalRecommendationType,
    GoalTitleEnum,
    GoalTypeEnum,
    GoalTypeTitlemapping,
    CallRunpipePropsType
} from '../../common/types';
import { Tabs } from '../../components/molecules';
import { TabVariant } from '../../components/molecules/Tabs/Tabs';
import GoalSummary from './GoalSummary';
import { styled } from '@mui/material';
import { AdviceGoalPayloadType } from '../../features/client-goals';
import { getClientJourneyFlowType, getEligibleGoals } from '../../utils';

const StyledContainer = styled('div')(({ theme }) => ({
    marginTop: theme.spacing(1.5)
}));

let persistentTabIndex = '0';

interface ReviewPlanDetailsProps {
    // eslint-disable-next-line no-unused-vars
    updateStep?: (step: number | ((prev: number) => number)) => void;
    isPDF?: boolean;
    callRunpipe?: CallRunpipePropsType;
    goalsWithNoFundingSource?: AdviceGoalPayloadType['formFields'];
}

const ReviewPlanDetails = ({ updateStep, isPDF, callRunpipe, goalsWithNoFundingSource }: ReviewPlanDetailsProps) => {
    const allFormFields = useAppSelector((state) => state.adviceGoals.formFields);
    const adviceGoals = cloneDeep(useAppSelector((state) => state.adviceGoals));
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const global = useAppSelector((state) => state.global);
    const tenant = useAppSelector((state) => state.auth?.user?.tenant || state.global?.globalConfig?.subdomain);
    const isFlow2 = getClientJourneyFlowType(tenant) === 'flow2';
    // Plan Overview must only display goals whose tenure has eligible funding sources.
    const adviceGoalsForms = isFlow2
        ? getEligibleGoals(
              adviceClient,
              { formFields: allFormFields },
              global?.orgInfo?.incomeSourceType?.en,
              global?.orgInfo?.investmentAccountType?.en
          )
        : allFormFields;
    const [activeTabIndex, setActiveTabIndex] = useState(() => {
        const persistentIndex = parseInt(persistentTabIndex);
        return persistentIndex < adviceGoalsForms.length ? persistentTabIndex : '0';
    });
    const { goeConfig } = cloneDeep(useAppSelector((state) => state.global));

    useEffect(() => {
        const persistentIndex = parseInt(persistentTabIndex);
        const currentIndex = parseInt(activeTabIndex);

        if (adviceGoalsForms.length > 0) {
            if (persistentIndex < adviceGoalsForms.length && persistentIndex !== currentIndex) {
                setActiveTabIndex(persistentTabIndex);
            } else if (persistentIndex >= adviceGoalsForms.length) {
                persistentTabIndex = '0';
                setActiveTabIndex('0');
            }
        }
    }, [adviceGoalsForms.length, adviceGoals]);

    const mappedGoals = adviceGoalsForms.map((item: any, index) => {
        const goalJson = adviceGoals?.response?.filter((itemResponse: any) => item.goalId == itemResponse?.goalId);
        const goalcalculatorJson = adviceGoals?.global?.responseGoalCalculator?.filter(
            (itemResponse: any) => item.goalId == itemResponse.goalId
        );
        const goalsData = adviceGoals?.request?.find((itemRequest: any) => item.goalId === itemRequest?.goalId);

        const goalDataRecomendation =
            adviceGoals?.recommendation?.find(
                (itemRequest: GoalRecommendationType) => item.goalId === itemRequest.goalId
            ) || {};

        // const goalPriorityValue = goeConfig['pipe_config']['goal_priority_prob_list'];
        const analysisReport = adviceGoals?.response?.find(
            (itemResponse: any) => item.goalId == itemResponse?.goalId
        )?.['response']?.['body']?.['analysisReport'];
        const needFixFlag = !analysisReport?.['meetGoalPriority'];
        //  old logic
        // analysisReport?.['currentGoalProbability'] * 100 <
        // goalPriorityValue?.[goalsData?.['goalPriority'] as 'Dream' | 'Want' | 'Wish' | 'Need'] * 100
        //     ? true
        //     : false;
        return {
            id: item.goalId,
            children: (
                <GoalSummary
                    key={item.goalId}
                    goalJson={goalJson}
                    goalsData={goalsData}
                    goeConfig={goeConfig}
                    goalcalculatorJson={goalcalculatorJson}
                    goalName={item.data.goalName}
                    goalType={item.data.wealthGoalType}
                    goalId={item.goalId}
                    goalIndex={index}
                    type={GoalTypeTitlemapping[item.data.wealthGoalType as GoalTitleEnum] as GoalTypeEnum}
                    isGoalRealistic={analysisReport?.['isGoalRealistic']}
                    isRetirement={item.data.goalType === 'WEALTH_GOAL' ? false : true}
                    goalDataRecomendation={goalDataRecomendation}
                    updateStep={updateStep}
                    isPDF={isPDF}
                    callRunpipe={callRunpipe}
                    goalsWithNoFundingSource={goalsWithNoFundingSource}
                />
            ),
            enabled: true,
            hidden: false,
            type: GoalTypeTitlemapping[item.data.wealthGoalType as GoalTitleEnum] as GoalTypeEnum,
            needFix: needFixFlag,
            title: item.data.goalName
        };
    });

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setActiveTabIndex(newValue);
        // Only persist the tab index when the feature flag is enabled
        persistentTabIndex = newValue;
    };
    // layout for PDF
    if (isPDF) return <div>{mappedGoals.map((g) => g.children)}</div>;

    return (
        <StyledContainer>
            <Tabs
                tabPanels={mappedGoals}
                value={activeTabIndex}
                variant={TabVariant.GOALS_TAB}
                onChange={handleTabChange}
            />
        </StyledContainer>
    );
};

export default ReviewPlanDetails;
